import i18n from "i18next"
import { initReactI18next } from "react-i18next"

import { isBrowser } from "../utils/common/isBrowser"
import de from "./locales/de"
import enGB from "./locales/en-GB"
import enUS from "./locales/en-US"
import esMX from "./locales/es-MX"
import fr from "./locales/fr"
import id from "./locales/id"
import ru from "./locales/ru"

// Locale codes, namespace layout and the `ttf_language` storage key all mirror
// ../admin/src/i18n/config.ts, so a translator moving between the dashboard and
// this storefront sees the same structure.
//
// The locale set matches the dashboard exactly. It is the platform's source of
// truth, so a storefront shipping a narrower set is the storefront being wrong.

const resources = {
  "es-MX": esMX,
  "en-GB": enGB,
  "en-US": enUS,
  fr,
  de,
  ru,
  id
}

// Order puts es-MX first because it is this storefront's default; the rest
// follow the dashboard's order. Endonyms are never translated.
export const SUPPORTED_LOCALES = [
  { code: "es-MX", label: "Español", flag: "🇲🇽" },
  { code: "en-GB", label: "English (UK)", flag: "🇬🇧" },
  { code: "en-US", label: "English (US)", flag: "🇺🇸" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "ru", label: "Русский", flag: "🇷🇺" },
  { code: "id", label: "Bahasa Indonesia", flag: "🇮🇩" }
]

// The brand name is DATA, not copy. Clients pick a country and a language; they
// never author or edit strings, so no translation may name a specific client.
// Every string refers to {{brand}} and i18next fills it from here.
export const BRAND_NAME = process.env.GATSBY_TTF_BRAND_NAME || "ClickPass"

// Single source of truth for the codes we ship, derived from the richer list
// above so the two can never drift.
const SUPPORTED_LNGS = SUPPORTED_LOCALES.map(locale => locale.code)

// ClickPass is a Mexican storefront, so Spanish is what a visitor gets when we
// can't match their browser language — and what every page is pre-rendered in.
export const DEFAULT_LOCALE = "es-MX"

export const STORAGE_KEY = "ttf_language"

export const NAMESPACES = ["common", "nav", "meta", "events", "checkout", "orders", "account", "errors"]

// Map a raw detected language (e.g. `es-ES`, `en-AU`, `fr-CA`) onto a locale we
// actually ship. Same shape as the dashboard's mapToSupportedLng.
export const mapToSupportedLng = lng => {
  if (!lng) return DEFAULT_LOCALE
  const base = lng.split("-")[0].toLowerCase()

  switch (base) {
    case "es":
      return "es-MX"
    case "en":
      return lng.toLowerCase() === "en-us" ? "en-US" : "en-GB"
    case "fr":
    case "de":
    case "ru":
    case "id":
      // Region variants (fr-CA, de-AT, …) collapse to the base locale we ship.
      return base
    default:
      // Nothing we ship — fall back to the storefront default.
      return DEFAULT_LOCALE
  }
}

i18n.use(initReactI18next).init({
  resources,
  // Register every namespace we ship so i18next doesn't fall back to its
  // default `ns: ['translation']`, a namespace we don't have — that failure
  // mode renders raw keys and looks like "the language didn't load".
  ns: NAMESPACES,
  defaultNS: "common",
  fallbackNS: "common",
  // Per-locale chains, not one global list. en-US ships only the handful of
  // keys whose spelling differs from en-GB (the same override-only approach the
  // legacy PHP app uses), so it MUST resolve through en-GB — a flat
  // [es-MX, en-GB] list would serve American visitors Spanish for every key
  // en-US doesn't override.
  fallbackLng: {
    "en-US": ["en-GB"],
    // Everything else falls back to en-GB, the source of truth, rather than to
    // es-MX — a missing French key should surface English, not Spanish.
    "es-MX": ["en-GB"],
    fr: ["en-GB"],
    de: ["en-GB"],
    ru: ["en-GB"],
    id: ["en-GB"],
    default: [DEFAULT_LOCALE, "en-GB"]
  },
  supportedLngs: SUPPORTED_LNGS,
  // Always initialise to the language the pages were pre-rendered in. The
  // visitor's real choice is applied after hydration — see initLanguage below.
  lng: DEFAULT_LOCALE,
  // Do NOT enable `nonExplicitSupportedLngs`: our bundles are keyed by
  // region-specific codes (es-MX, en-GB, en-US) and that option collapses the
  // resolution hierarchy, after which namespaced lookups like t('nav:key')
  // resolve to nothing and whole locales appear "not loading".
  interpolation: {
    escapeValue: false,
    // Supplies {{brand}} to every string without each call site passing it.
    defaultVariables: { brand: BRAND_NAME }
  },
  react: { useSuspense: false }
})

/**
 * Resolve the visitor's language from their stored choice, then their browser.
 *
 * This is deliberately NOT i18next-browser-languagedetector wired in at init
 * (which is what the dashboard does). The dashboard is a client-only SPA; these
 * pages are pre-rendered by Gatsby in Spanish. Detecting at init would make the
 * client's first render disagree with the server HTML and trip a React
 * hydration mismatch, so detection is applied in an effect after mount instead.
 */
export const initLanguage = () => {
  if (!isBrowser) return

  let next
  try {
    next = window.localStorage.getItem(STORAGE_KEY)
  } catch {
    // Safari private mode and similar — fall through to navigator.
    next = null
  }

  if (!next) {
    const fromBrowser = window.navigator.languages?.[0] || window.navigator.language
    next = mapToSupportedLng(fromBrowser)
  }

  if (next && next !== i18n.resolvedLanguage) i18n.changeLanguage(next)
}

// Persist an explicit choice so it wins on the visitor's next visit.
if (isBrowser) {
  i18n.on("languageChanged", lng => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lng)
    } catch {
      // Storage unavailable — the choice just won't survive a reload.
    }
  })
}

export default i18n
